import React, { useMemo } from 'react'
import { Box, Text, useDisclosure, useToast } from '@chakra-ui/react'
import { LuBan } from 'react-icons/lu'

import { useChat } from '../chat-context'
import SharedModal from '~components/shared/modals/shared-modal'
import {
    namedOperations,
    useGetClinicQuery,
    useUpdateClinicMutation,
} from '~graphql/generated/graphql'
import { useClinic } from '~contexts/clinic-context'
import {
    cleanPhoneDigits,
    formatPhoneForStorage,
    formatPhoneReadable,
} from '~utils/phone'
import { getPhoneCountryFromCode } from '~utils/helpers'
import MenuItem from '~components/shared/menu/menu-item'

export default function BlockPhoneNumberAction() {
    const { chat, clientPhone, baseClientPhone } = useChat()
    const { clinic: selectedClinic, setClinic } = useClinic()
    const { isOpen, onOpen, onClose } = useDisclosure()
    const toast = useToast()

    const isSelectedClinic = selectedClinic?.id === chat.clinic.id
    const { data, loading: loadingClinic } = useGetClinicQuery({
        variables: {
            id: chat.clinic.id,
        },
        skip: isSelectedClinic,
    })

    const clinic = isSelectedClinic ? selectedClinic : data?.clinic
    const rawPhone = clientPhone?.phone || baseClientPhone
    const countryHint = clientPhone?.country_code
        ? getPhoneCountryFromCode(clientPhone.country_code)
        : chat.clinic.address?.country
    const phoneToBlock = getBlockedPhoneNumberValue(rawPhone, countryHint)
    const blockedPhoneNumbers = useMemo(
        () => clinic?.settings?.sms_block_list ?? [],
        [clinic?.settings?.sms_block_list],
    )
    const isBlocked = useMemo(() => {
        if (!phoneToBlock) return false

        return blockedPhoneNumbers.some(
            phoneNumber =>
                getBlockedPhoneNumberValue(phoneNumber, countryHint) ===
                phoneToBlock,
        )
    }, [blockedPhoneNumbers, countryHint, phoneToBlock])

    const displayPhone =
        formatPhoneReadable(phoneToBlock, countryHint) || rawPhone || 'number'
    const actionText = isBlocked ? 'Unblock Number' : 'Block Number'
    const isDisabled = !phoneToBlock || !clinic || loadingClinic

    const [updateClinic, { loading }] = useUpdateClinicMutation({
        onCompleted: data => {
            if (selectedClinic?.id === data.updateClinic.id) {
                setClinic(data.updateClinic)
            }

            toast({
                status: 'success',
                description: isBlocked
                    ? `${displayPhone} was removed from blocked numbers.`
                    : `${displayPhone} was added to blocked numbers.`,
                position: 'top',
            })
            onClose()
        },
        onError: () => {
            toast({
                status: 'error',
                description: isBlocked
                    ? 'Failed to unblock phone number.'
                    : 'Failed to block phone number.',
                position: 'top',
            })
        },
        refetchQueries: [namedOperations.Query.getClinic],
        awaitRefetchQueries: true,
    })

    async function handleConfirm() {
        if (!phoneToBlock) return

        const nextBlockedPhoneNumbers = isBlocked
            ? blockedPhoneNumbers.filter(
                  phoneNumber =>
                      getBlockedPhoneNumberValue(phoneNumber, countryHint) !==
                      phoneToBlock,
              )
            : [...blockedPhoneNumbers, phoneToBlock]

        await updateClinic({
            variables: {
                id: chat.clinic.id,
                data: {
                    settings: {
                        sms_block_list: nextBlockedPhoneNumbers,
                    },
                },
            },
        })
    }

    return (
        <>
            <MenuItem
                icon={LuBan}
                onClick={onOpen}
                isDisabled={isDisabled}
                iconProps={{
                    color: isBlocked ? 'red.500' : undefined,
                }}
            >
                {phoneToBlock
                    ? `${actionText}: ${displayPhone}`
                    : 'No phone number to block'}
            </MenuItem>
            <SharedModal
                isOpen={isOpen}
                onClose={onClose}
                onCancel={onClose}
                onConfirm={handleConfirm}
                confirmText={actionText}
                title={actionText}
                isLoading={loading}
                skipCloseOnConfirm
            >
                <Box>
                    <Text>
                        {isBlocked
                            ? `Remove ${displayPhone} from this clinic's blocked numbers?`
                            : `Add ${displayPhone} to this clinic's blocked numbers? Messages from this number will be automatically blocked.`}
                    </Text>
                </Box>
            </SharedModal>
        </>
    )
}

function getBlockedPhoneNumberValue(
    phone: string | null | undefined,
    countryHint?: string | null,
): string | null {
    if (!phone) return null

    // Fall back to the raw sender value so alphanumeric / non-standard
    // senders (which don't normalize to digits) can still be blocked.
    return (
        formatPhoneForStorage(phone, countryHint) ||
        cleanPhoneDigits(phone) ||
        phone.trim() ||
        null
    )
}
